import {
  DriverType,
  ExcludedDriverType,
  VersionedObjectDiff,
} from '@clearcover/core-app-types';
import React, { FC, useContext } from 'react';
import { CardSectionInfoPairs } from 'Shared/components';
import { EMDASH, formatDateShort, formatMoney } from 'Shared/utils';
import { Container, Pill } from '@clearcover/atlas';
import { STATE_OPTIONS, VALID_US_STATES_TYPE } from 'Shared/constants/states';
import styles from './styles.module.css';
import { formatDiff } from 'Policies/components/PolicyActivities/Common/Common';
import { InfoValuePairDataType } from 'Shared/components/Card/CardSectionInfoPairs/InfoPair';
import { PolicyContext } from 'Policies/utils';
import { env } from 'Shared/constants/env';

type DriverAccidentType = DriverType['accidents'][number];

type DriverDetailsDiff = {
  diff: VersionedObjectDiff<DriverType | ExcludedDriverType>;
};
type CoveredDriverDetailsDiff = {
  oldDriver?: DriverType;
  newDriver: DriverType;
};
type ExcludedDriverDetailsDiff = {
  oldDriver?: ExcludedDriverType;
  newDriver: ExcludedDriverType;
};
type HeaderAndData = {
  headers: string[];
  data: InfoValuePairDataType[];
};

// Helper to convert boolean/truthy values to Yes/No strings
const formatBooleanToYesNo = (
  value: boolean | string | undefined | null
): string | undefined => {
  if (value === undefined || value === null) return undefined;
  return value ? 'Yes' : 'No';
};

// Helper to safely format prior policy dates with validation
const formatPriorPolicyDate = (dateStr: string | null | undefined): string => {
  if (!dateStr || typeof dateStr !== 'string') return EMDASH;
  // Return as-is if already formatted, otherwise fallback to EMDASH
  return dateStr.trim() || EMDASH;
};

// Exposure details configuration - maps config flags to field keys and labels
// Defined outside component since it's static
const EXPOSURE_DETAILS_CONFIG = [
  {
    configKey: 'askLivesInHousehold',
    fieldKey: 'livesWithPolicyholder',
    label: 'Lives in primary household',
  },
  {
    configKey: 'askHasAccessToVehicles',
    fieldKey: 'hasAccessToVehicles',
    label:
      'Regularly drives, or has regular access to any of the insured vehicles',
  },
  {
    configKey: 'askHasOwnInsurancePolicy',
    fieldKey: 'hasOwnAutoPolicy',
    label: 'Has their own insurance policy with Clearcover or another carrier',
  },
] as const;

export const PolicyChangeDriverDetails: FC<DriverDetailsDiff> = ({ diff }) => {
  /*this may be confusing, here the reason:
    driver to show default will be the plain static values to show.
    When diff type is UPDATED we want to show the diff mode (oldValue -> newValue)
    so the driver to diff against object is there to control when that should show*/
  const driverToShowDefault =
    diff.type === 'ADDED' || diff.type === 'UPDATED' ? diff.new : diff.old;
  const driverToDiffAgainst =
    diff.type === 'UPDATED' ? diff.old : driverToShowDefault;
  if (isCoveredDriver(driverToShowDefault)) {
    return (
      <PolicyChangeCoveredDriverDetails
        newDriver={driverToShowDefault as DriverType}
        oldDriver={driverToDiffAgainst as DriverType}
      />
    );
  }

  return (
    <PolicyChangeExcludedDriverDetails
      newDriver={driverToShowDefault}
      oldDriver={driverToDiffAgainst as ExcludedDriverType}
    />
  );
};

const extractHeadersAndValues = (
  propertiesNames: string[],
  newObject: any,
  oldObject?: any
): HeaderAndData => {
  const headers = propertiesNames.map((propName) => newObject[propName].title);
  const data = propertiesNames.map((propName) =>
    formatDiff(
      oldObject ? oldObject[propName].value : null,
      newObject[propName].value
    )
  );
  return { headers, data };
};

const PolicyChangeCoveredDriverDetails: FC<CoveredDriverDetailsDiff> = ({
  oldDriver,
  newDriver,
}) => {
  const {
    driverOptions,
    details,
    details: { mailingState, showStudentSection },
  } = useContext(PolicyContext);

  const driverOptionsQuestionsConfig =
    driverOptions?.driverOptionsQuestionsConfig;

  // Build exposure details headers and data based on config
  const getExposureDetailsHeadersAndData = (): HeaderAndData => {
    if (!env.driverNewQuestionsEnabled || !driverOptionsQuestionsConfig) {
      return { headers: [], data: [] };
    }

    const headers: string[] = [];
    const data: InfoValuePairDataType[] = [];

    EXPOSURE_DETAILS_CONFIG.forEach(({ configKey, fieldKey, label }) => {
      if (
        driverOptionsQuestionsConfig[
          configKey as keyof typeof driverOptionsQuestionsConfig
        ]
      ) {
        headers.push(label);
        const newValue = newDriver.exposureDetails?.[fieldKey];
        const oldValue = oldDriver?.exposureDetails?.[fieldKey];
        data.push(
          formatDiff(
            formatBooleanToYesNo(oldValue),
            formatBooleanToYesNo(newValue)
          )
        );
      }
    });

    return { headers, data };
  };

  const exposureDetailsHeadersAndData = getExposureDetailsHeadersAndData();

  const residencyDetailsMap = {
    inStateResidencyDuration: `In a typical year, does this driver reside in ${mailingState} for at least 10 months?`,
    isOutOfStateStudent: 'Full-time student away at school',
    isStateResidentWithOutOfStateHomes: `Permanent resident of ${mailingState} with multiple households in multiple states`,
    hasVehicleWhileOutOfStateStudent: `Has possession of an insured vehicle under this policy while attending school outside of ${mailingState}`,
  };

  const residencyDetailsToInfoPair = (
    residencyDetails: Record<string, string | boolean | undefined>
  ) => {
    return Object.entries(residencyDetails).reduce(
      (acc, [key, value]) => {
        const title =
          residencyDetailsMap[key as keyof typeof residencyDetailsMap];
        if (title) {
          // Only include if we have a valid title mapping
          acc[key] = {
            title,
            value: formatBooleanToYesNo(value) ?? 'No',
            dataValue: value,
          };
        }
        return acc;
      },
      {} as Record<
        string,
        {
          title: string;
          value: string;
          dataValue: string | boolean | undefined;
        }
      >
    );
  };

  // Filter out attributes based on config and duplicates
  const {
    militaryRank: _militaryRank,
    isDeployed: _isDeployed,
    // Conditionally exclude educationLevel and occupation based on config
    ...remainingAttributes
  } = newDriver.attributes;

  // Build filtered attributes based on config flags
  const askForEducation = driverOptionsQuestionsConfig?.askForEducation ?? true;
  const askForOccupation =
    driverOptionsQuestionsConfig?.askForOccupation ?? true;
  const askForAgeFirstLicensed =
    driverOptionsQuestionsConfig?.askForAgeFirstLicensed ?? true;

  const driverAttributes = Object.fromEntries(
    Object.entries(remainingAttributes).filter(([key]) => {
      if (key === 'educationLevel' && !askForEducation) return false;
      if (key === 'occupation' && !askForOccupation) return false;
      if (key === 'bankruptcy') return false;
      return true;
    })
  );

  let driverAttributesNames = Object.getOwnPropertyNames(driverAttributes);

  const askStateResidencyDetails =
    driverOptionsQuestionsConfig?.askStateResidencyDetails;
  const stateResidencyDetailsAttributes = residencyDetailsToInfoPair(
    newDriver.residencyDetails ?? {}
  );

  if (askStateResidencyDetails && env.driverNewQuestionsEnabled) {
    driverAttributesNames = driverAttributesNames.concat(
      Object.getOwnPropertyNames(stateResidencyDetailsAttributes)
    );
  }

  // Filter old driver attributes the same way
  const filterAttributes = (
    attributes: Record<string, any> | undefined
  ): Record<string, any> => {
    if (!attributes) return {};
    return Object.fromEntries(
      Object.entries(attributes).filter(([key]) => {
        if (key === 'militaryRank' || key === 'isDeployed') return false;
        if (key === 'educationLevel' && !askForEducation) return false;
        if (key === 'occupation' && !askForOccupation) return false;
        return true;
      })
    );
  };

  const newDriverAttributes = {
    ...filterAttributes(newDriver.attributes),
    ...(askStateResidencyDetails && env.driverNewQuestionsEnabled
      ? { ...stateResidencyDetailsAttributes }
      : {}),
  };
  const oldDriverAttributes = {
    ...filterAttributes(oldDriver?.attributes),
    ...(askStateResidencyDetails && env.driverNewQuestionsEnabled
      ? { ...residencyDetailsToInfoPair(oldDriver?.residencyDetails ?? {}) }
      : {}),
  };

  const driverHeadersAndValues = extractHeadersAndValues(
    driverAttributesNames,
    newDriverAttributes,
    oldDriverAttributes
  );

  const militaryPropertiesNames = Object.getOwnPropertyNames(
    newDriver.military
  );
  const militaryHeadersAndValues = extractHeadersAndValues(
    militaryPropertiesNames,
    newDriver.military,
    oldDriver?.military
  );

  const studentPropertiesNames = Object.getOwnPropertyNames(newDriver.student);
  const studentHeadersAndValues = extractHeadersAndValues(
    studentPropertiesNames,
    newDriver.student,
    oldDriver?.student
  );

  return (
    <div data-testid="covered-driver-details">
      <Container>
        <h4>Driver Information</h4>
        <CardSectionInfoPairs
          headers={[
            'First Name',
            'Middle Initial',
            'Last Name',
            'Suffix',
            'Gender',
            'Marital status',
            'Date of Birth',
            'Relation to Applicant',
            'Driver License Number',
            'Driver License Issued State',
            'Driver License Status',
            ...exposureDetailsHeadersAndData.headers,
          ]}
          data={[
            formatDiff(
              oldDriver?.person.name.firstName,
              newDriver.person.name.firstName
            ),
            formatDiff(
              oldDriver?.person.name.middleName,
              newDriver.person.name.middleName
            ),
            formatDiff(
              oldDriver?.person.name.lastName,
              newDriver.person.name.lastName
            ),
            formatDiff(oldDriver?.person.suffix, newDriver.person.suffix),
            formatDiff(oldDriver?.person.gender, newDriver.person.gender),
            formatDiff(
              oldDriver?.person.maritalStatus,
              newDriver.person.maritalStatus
            ),
            formatDiff(
              oldDriverDateOfBirth(oldDriver),
              formatDateShort(newDriver.person.dateOfBirth)
            ),
            formatDiff(
              oldDriver?.relationToApplicant,
              newDriver.relationToApplicant
            ),
            formatDiff(
              oldDriver?.driversLicense.number,
              newDriver.driversLicense.number
            ),
            formatDiff(
              STATE_OPTIONS[
                oldDriver?.driversLicense.state as VALID_US_STATES_TYPE
              ],
              STATE_OPTIONS[
                newDriver.driversLicense.state as VALID_US_STATES_TYPE
              ]
            ),
            formatDiff(
              oldDriver?.driversLicense.status,
              newDriver.driversLicense.status
            ),
            ...exposureDetailsHeadersAndData.data,
          ]}
          className={styles.details}
        />
      </Container>
      <Container>
        <h4>Driver Attributes</h4>
        <CardSectionInfoPairs
          headers={driverHeadersAndValues.headers.concat(
            askForAgeFirstLicensed ? 'Age First Licensed' : []
          )}
          data={driverHeadersAndValues.data.concat(
            askForAgeFirstLicensed
              ? formatDiff(
                  oldDriver?.ageFirstLicensed,
                  newDriver.ageFirstLicensed
                )
              : []
          )}
          className={styles.details}
        />
      </Container>
      {newDriver.military && details.showMilitarySection && (
        <Container>
          <h4>Military Info</h4>
          <CardSectionInfoPairs
            headers={militaryHeadersAndValues.headers}
            data={militaryHeadersAndValues.data}
            className={styles.details}
          />
        </Container>
      )}
      {newDriver.student && showStudentSection && (
        <Container>
          <h4>Student</h4>
          <CardSectionInfoPairs
            headers={studentHeadersAndValues.headers}
            data={studentHeadersAndValues.data}
            className={styles.details}
          />
        </Container>
      )}

      {/* Assigned Vehicles - Only shown for US-VA region */}
      {details.region === 'US-VA' && newDriver.assignedVehicles && (
        <Container>
          <h4>Assigned Vehicles</h4>
          <CardSectionInfoPairs
            headers={['Assigned Vehicles']}
            data={[
              formatDiff(
                formatAssignedVehicles(oldDriver?.assignedVehicles),
                formatAssignedVehicles(newDriver.assignedVehicles)
              ),
            ]}
            className={styles.details}
          />
        </Container>
      )}

      {/* Driver Reports Section */}
      <DriverReportsSection oldDriver={oldDriver} newDriver={newDriver} />
    </div>
  );
};

const oldDriverDateOfBirth = (
  oldDriver: DriverType | ExcludedDriverType | undefined
) =>
  oldDriver?.person.dateOfBirth
    ? formatDateShort(oldDriver?.person.dateOfBirth)
    : '';

const PolicyChangeExcludedDriverDetails: FC<ExcludedDriverDetailsDiff> = ({
  newDriver,
  oldDriver,
}) => {
  const { driverOptions } = useContext(PolicyContext);

  const excludedDriverOptionsQuestionsConfig =
    driverOptions?.excludedDriverOptionsQuestionsConfig;

  // Build headers and data dynamically based on config
  const headers: string[] = [
    'First Name',
    'Middle Initial',
    'Last Name',
    'Date of Birth',
  ];
  const data: InfoValuePairDataType[] = [
    formatDiff(
      oldDriver?.person.name.firstName,
      newDriver.person.name.firstName
    ),
    formatDiff(
      oldDriver?.person.name.middleName,
      newDriver.person.name.middleName
    ),
    formatDiff(oldDriver?.person.name.lastName, newDriver.person.name.lastName),
    formatDiff(
      oldDriverDateOfBirth(oldDriver),
      formatDateShort(newDriver.person.dateOfBirth)
    ),
  ];

  // Add Gender if configured
  if (excludedDriverOptionsQuestionsConfig?.askForGender) {
    headers.push('Gender');
    data.push(formatDiff(oldDriver?.person.gender, newDriver.person.gender));
  }

  // Add Marital Status if configured
  if (excludedDriverOptionsQuestionsConfig?.askForMaritalStatus) {
    headers.push('Marital Status');
    data.push(
      formatDiff(
        oldDriver?.person.maritalStatus,
        newDriver.person.maritalStatus
      )
    );
  }

  return (
    <div data-testid="excluded-driver-details">
      <Container>
        <h4>
          Driver Information{' '}
          <Pill variant="disabled" className={styles.pill}>
            {newDriver.policyPartyRelationship === 'listed_person'
              ? 'List Only'
              : 'Excluded'}
          </Pill>
        </h4>
        <CardSectionInfoPairs
          headers={headers}
          data={data}
          className={styles.details}
        />
      </Container>
    </div>
  );
};

// Driver Reports Section Component
const DriverReportsSection: FC<{
  oldDriver?: DriverType;
  newDriver: DriverType;
}> = ({ oldDriver, newDriver }) => {
  const { details, driverOptions } = useContext(PolicyContext);

  const canChallengePriorPolicy =
    driverOptions?.priorPolicy?.canChallenge ?? false;

  // Prior Insurance headers and data
  const priorInsuranceHeaders = ['Source', 'Carrier', 'Duration', 'BI Limits'];
  const priorInsuranceData: InfoValuePairDataType[] = [
    formatDiff(
      oldDriver?.priorCarrier?.source || EMDASH,
      newDriver.priorCarrier?.source || EMDASH
    ),
    formatDiff(
      oldDriver?.priorCarrier?.carrier || EMDASH,
      newDriver.priorCarrier?.carrier || EMDASH
    ),
    formatDiff(
      formatDuration(oldDriver?.priorCarrier),
      formatDuration(newDriver.priorCarrier)
    ),
    formatDiff(
      oldDriver?.priorCarrier?.bodyInjuryLimits || EMDASH,
      newDriver.priorCarrier?.bodyInjuryLimits || EMDASH
    ),
  ];

  // Accidents headers and data
  const accidentsHeaders = [
    'Source',
    'Accident Date',
    'At Fault',
    'Damage Amount',
    'Claim Type',
  ];

  // Build merged accident list so removed accidents (AC04/AC05) remain visible
  const mergedAccidents = (() => {
    const byId = new Map<
      string,
      { newAcc?: DriverAccidentType; oldAcc?: DriverAccidentType }
    >();
    newDriver.accidents?.forEach((a) => byId.set(a.id, { newAcc: a }));
    oldDriver?.accidents?.forEach((a) => {
      const entry = byId.get(a.id);
      if (entry) entry.oldAcc = a;
      else byId.set(a.id, { oldAcc: a });
    });
    return Array.from(byId.values());
  })();

  // Violations headers and data
  const violationsHeaders = [
    'Source',
    'Violation Date',
    'Conviction Date',
    'Description',
  ];

  return (
    <>
      {/* Prior Insurance */}

      {/* Prior Policies - shown when canChallengePriorPolicy is true */}
      {canChallengePriorPolicy && newDriver.priorPolicies ? (
        <Container>
          <h4>Prior carrier</h4>
          {newDriver.priorPolicies.length > 0 ? (
            newDriver.priorPolicies.map((priorPolicy, index) => {
              // Match prior policies using multiple strategies for robustness:
              // 1. Primary: Match by policyNumber if both have it
              // 2. Fallback: Match by carrier + start date combination (handles missing/changed policy numbers)
              const oldPriorPolicy = oldDriver?.priorPolicies?.find((old) => {
                // Try matching by policy number first (most reliable if present)
                if (priorPolicy.policyNumber && old.policyNumber) {
                  return old.policyNumber === priorPolicy.policyNumber;
                }
                // Fallback: match by carrier + start date combination
                return (
                  old.currentCarrierName === priorPolicy.currentCarrierName &&
                  old.startDate === priorPolicy.startDate
                );
              });
              return (
                <CardSectionInfoPairs
                  key={priorPolicy.policyNumber || index}
                  headers={[
                    'Source',
                    'Carrier',
                    'Policy Number',
                    'Effective Date',
                    'End Date',
                    'State',
                    'BI Limit',
                    'CSL Limit',
                    'PIP Limit',
                  ]}
                  data={[
                    formatDiff(
                      oldPriorPolicy?.source || EMDASH,
                      priorPolicy.source || EMDASH
                    ),
                    formatDiff(
                      oldPriorPolicy?.currentCarrierName || EMDASH,
                      priorPolicy.currentCarrierName || EMDASH
                    ),
                    formatDiff(
                      oldPriorPolicy?.policyNumber || EMDASH,
                      priorPolicy.policyNumber || EMDASH
                    ),
                    formatDiff(
                      formatPriorPolicyDate(oldPriorPolicy?.startDate),
                      formatPriorPolicyDate(priorPolicy.startDate)
                    ),
                    formatDiff(
                      formatPriorPolicyDate(oldPriorPolicy?.endDate),
                      formatPriorPolicyDate(priorPolicy.endDate)
                    ),
                    formatDiff(
                      oldPriorPolicy?.state || EMDASH,
                      priorPolicy.state || EMDASH
                    ),
                    formatDiff(
                      oldPriorPolicy?.coverages?.bodilyInjury || EMDASH,
                      priorPolicy.coverages?.bodilyInjury || EMDASH
                    ),
                    formatDiff(
                      oldPriorPolicy?.coverages?.combinedSingleLimit || EMDASH,
                      priorPolicy.coverages?.combinedSingleLimit || EMDASH
                    ),
                    formatDiff(
                      oldPriorPolicy?.coverages?.personalInjuryProtection ||
                        EMDASH,
                      priorPolicy.coverages?.personalInjuryProtection || EMDASH
                    ),
                  ]}
                  className={styles.details}
                />
              );
            })
          ) : (
            <p>-</p>
          )}
        </Container>
      ) : (
        <Container>
          <h4>Prior Insurance</h4>
          <CardSectionInfoPairs
            headers={priorInsuranceHeaders}
            data={priorInsuranceData}
            className={styles.details}
          />
        </Container>
      )}

      {/* Certificate of Financial Responsibility (SR-22/FR-44) - shown when showDriverCfrs is true */}
      {details?.showDriverCfrs &&
        newDriver.cfrs &&
        newDriver.cfrs.length > 0 && (
          <Container>
            <h4>Certificate of Financial Responsibility (SR-22/FR-44)</h4>
            {newDriver.cfrs.map((cfr) => {
              // Match by caseNumber and state combination for stable diff
              const oldCfr = oldDriver?.cfrs?.find(
                (old) =>
                  old.caseNumber === cfr.caseNumber && old.state === cfr.state
              );
              return (
                <CardSectionInfoPairs
                  key={`${cfr.caseNumber}-${cfr.state}`}
                  headers={['Source', 'Certificate', 'Case Number', 'State']}
                  data={[
                    formatDiff(oldCfr?.source || EMDASH, cfr.source || EMDASH),
                    formatDiff(oldCfr?.type || 'None', cfr.type || 'None'),
                    formatDiff(
                      oldCfr?.caseNumber || EMDASH,
                      cfr.caseNumber || EMDASH
                    ),
                    formatDiff(
                      STATE_OPTIONS[oldCfr?.state as VALID_US_STATES_TYPE] ||
                        EMDASH,
                      STATE_OPTIONS[cfr.state as VALID_US_STATES_TYPE] || EMDASH
                    ),
                  ]}
                  className={styles.details}
                />
              );
            })}
          </Container>
        )}

      {/* SR-22 (legacy) - shown when showDriverCfrs is false */}
      {!details?.showDriverCfrs && newDriver.sr22s && (
        <Container>
          <h4>SR-22</h4>
          {newDriver.sr22s.length > 0 ? (
            newDriver.sr22s.map((sr22) => {
              // Match by id for stable diff
              const oldSr22 = oldDriver?.sr22s?.find(
                (old) => old.id === sr22.id
              );
              return (
                <CardSectionInfoPairs
                  key={sr22.id}
                  headers={['Source', 'Start Date', 'End Date']}
                  data={[
                    formatDiff(
                      oldSr22?.source || EMDASH,
                      sr22.source || EMDASH
                    ),
                    formatDiff(
                      oldSr22?.startDate
                        ? formatDateShort(oldSr22.startDate)
                        : EMDASH,
                      sr22.startDate ? formatDateShort(sr22.startDate) : EMDASH
                    ),
                    formatDiff(
                      oldSr22?.endDate
                        ? formatDateShort(oldSr22.endDate)
                        : EMDASH,
                      sr22.endDate ? formatDateShort(sr22.endDate) : EMDASH
                    ),
                  ]}
                  className={styles.details}
                />
              );
            })
          ) : (
            <CardSectionInfoPairs
              headers={['Has SR-22']}
              data={[formatDiff('No', 'No')]}
              className={styles.details}
            />
          )}
        </Container>
      )}

      {/* Accidents */}
      <Container>
        <h4>Accidents</h4>
        {mergedAccidents.length > 0 ? (
          mergedAccidents.map(({ newAcc, oldAcc }) => {
            const acc = newAcc ?? oldAcc!;
            return (
              <CardSectionInfoPairs
                key={acc.id}
                headers={accidentsHeaders}
                data={[
                  formatDiff(
                    oldAcc?.source || EMDASH,
                    newAcc?.source || EMDASH
                  ),
                  formatDiff(
                    oldAcc?.accidentDate
                      ? formatDateShort(oldAcc.accidentDate)
                      : EMDASH,
                    newAcc?.accidentDate
                      ? formatDateShort(newAcc.accidentDate)
                      : EMDASH
                  ),
                  formatDiff(
                    formatBooleanToYesNo(oldAcc?.isAtFault) || EMDASH,
                    formatBooleanToYesNo(newAcc?.isAtFault) || EMDASH
                  ),
                  formatDiff(
                    oldAcc?.damageAmount
                      ? formatMoney(oldAcc.damageAmount, {
                          includeCents: true,
                        })
                      : EMDASH,
                    newAcc?.damageAmount
                      ? formatMoney(newAcc.damageAmount, {
                          includeCents: true,
                        })
                      : EMDASH
                  ),
                  formatDiff(
                    formatAccidentClaimType(oldAcc),
                    formatAccidentClaimType(newAcc)
                  ),
                ]}
                className={styles.details}
              />
            );
          })
        ) : (
          <p>No accidents found</p>
        )}
      </Container>

      {/* Violations */}
      <Container>
        <h4>Violations</h4>
        {newDriver.violations && newDriver.violations.length > 0 ? (
          newDriver.violations.map((violation) => {
            // Match by id for stable diff
            const oldViolation = oldDriver?.violations?.find(
              (old) => old.id === violation.id
            );
            return (
              <CardSectionInfoPairs
                key={violation.id}
                headers={violationsHeaders}
                data={[
                  formatDiff(
                    oldViolation?.source || EMDASH,
                    violation.source || EMDASH
                  ),
                  formatDiff(
                    oldViolation?.violationDate
                      ? formatDateShort(oldViolation.violationDate)
                      : EMDASH,
                    violation.violationDate
                      ? formatDateShort(violation.violationDate)
                      : EMDASH
                  ),
                  formatDiff(
                    oldViolation?.convictionDate
                      ? formatDateShort(oldViolation.convictionDate)
                      : EMDASH,
                    violation.convictionDate
                      ? formatDateShort(violation.convictionDate)
                      : EMDASH
                  ),
                  formatDiff(
                    oldViolation?.description || EMDASH,
                    violation.description || EMDASH
                  ),
                ]}
                className={styles.details}
              />
            );
          })
        ) : (
          <p>No violations found</p>
        )}
      </Container>
    </>
  );
};

// Helper to format assigned vehicles array
// Distinguishes between: undefined/null (EMDASH), empty array ("None"), array with items (joined list)
const formatAssignedVehicles = (
  vehicles: string[] | undefined | null
): string => {
  if (vehicles === undefined || vehicles === null) {
    return EMDASH;
  }
  if (vehicles.length === 0) {
    return 'None';
  }
  return vehicles.join(', ');
};

// Helper to format prior carrier duration
const formatDuration = (
  priorCarrier:
    | {
        wasPreviouslyInsured?: boolean;
        currentlyInsuredMonths?: number;
        daysSinceLastInsured?: number;
      }
    | undefined
): string => {
  if (!priorCarrier) return EMDASH;
  // Explicitly check for null/undefined to handle 0 as a valid value
  if (
    priorCarrier.wasPreviouslyInsured &&
    priorCarrier.currentlyInsuredMonths !== null &&
    priorCarrier.currentlyInsuredMonths !== undefined
  ) {
    return `${priorCarrier.currentlyInsuredMonths} months`;
  }
  if (
    priorCarrier.daysSinceLastInsured !== null &&
    priorCarrier.daysSinceLastInsured !== undefined
  ) {
    return `${priorCarrier.daysSinceLastInsured} days since last insured`;
  }
  return EMDASH;
};

function isCoveredDriver(
  driver: DriverType | ExcludedDriverType
): driver is DriverType {
  return 'driversLicense' in driver;
}

const formatAccidentClaimType = (
  accident: DriverAccidentType | undefined
): string => {
  if (!accident?.classifications) return EMDASH;
  const { lendingLoss, claimClassificationTypeV2 } = accident.classifications;
  if (lendingLoss) {
    return claimClassificationTypeV2
      ? `Lending Loss - ${claimClassificationTypeV2}`
      : 'Lending Loss';
  }
  return claimClassificationTypeV2 || EMDASH;
};
